import { Flex } from "@chakra-ui/react";
import { FormattedMessage, useIntl } from "react-intl";

import { RanaLoading, RanaModal } from "@nens/ranalib";
import { FormErrors, RanaForm, SubmitFunction } from "@nens/ranalib";

import { cancelButtonText } from "@/translations";
import { containsInvalidCharacters, getFirstInvalidCharacter } from "@/utils/invalid-characters";

import { useAddFolder } from "../api/add-folder";

interface AddFolderModalProps {
  basePath: string;
  onClose: () => void;
  existingFileIds: string[];
}

type FolderFormValues = {
  folderName: string;
};

export function AddFolderModal({ basePath, onClose, existingFileIds }: AddFolderModalProps) {
  const intl = useIntl();
  const { isMutating, mutate } = useAddFolder(basePath);

  const validate = ({ folderName }: FolderFormValues): FormErrors => {
    if (existingFileIds.includes(basePath + folderName + "/")) {
      return {
        folderName: intl.formatMessage({
          id: "foldererrors.name_existed",
          defaultMessage: "Folder name already exists",
        }),
      };
    }

    const notAllowedCharacter = containsInvalidCharacters(folderName);
    if (notAllowedCharacter)
      return {
        folderName: intl.formatMessage(
          {
            id: "foldererrors.not_allowed_character",
            defaultMessage: `Character "{character}" is not allowed`,
          },
          {
            character: getFirstInvalidCharacter(folderName),
          }
        ),
      };

    return {};
  };

  const submit: SubmitFunction<FolderFormValues> = async (validatedValues, onError) => {
    mutate(validatedValues.folderName, {
      onSuccess: onClose,
      onError,
    });
  };

  return (
    <RanaForm validate={validate} doSubmit={submit}>
      <RanaModal
        isOpen
        isCentered
        onClose={onClose}
        topBar={intl.formatMessage({
          id: "form.create_new_folder",
          defaultMessage: "Create new folder",
        })}
        buttons={[
          {
            children: cancelButtonText,
            variant: "tertiary",
            isDisabled: isMutating,
            onClick: onClose,
          },
          <RanaForm.SubmitButton key="submit" isDisabled={isMutating}>
            <FormattedMessage id="button.create" defaultMessage="Create" />
          </RanaForm.SubmitButton>,
        ]}
      >
        <Flex flexDir="column" gap={2}>
          <RanaForm.Input
            submitOnEnter
            autoFocus
            field="folderName"
            explanation={intl.formatMessage({
              id: "form_input.folder_name",
              defaultMessage: "Folder name",
            })}
            isRequired
          />
          {isMutating && <RanaLoading size="md" isCentered />}
          <RanaForm.Errors />
        </Flex>
      </RanaModal>
    </RanaForm>
  );
}
